import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { JOBS_PORTAL_URL } from '../../Services/Services';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import ClipLoader from 'react-spinners/ClipLoader';
import './ManageJobs.css';

const API_URL = `${JOBS_PORTAL_URL}/api/jobs`;

const ManageJobs = () => {
    const [jobs, setJobs] = useState([]);
    const [editId, setEditId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        company: '',
        location: '',
        type: '',
        no_of_openings: 0,
        clientEmail: '',
        ctc: ''
    });
    const [originalData, setOriginalData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('PENDING');

    const navigate = useNavigate();
    const userId = localStorage.getItem('userId');

    useEffect(() => {
        if (!userId) {
            navigate("/login");
        }
    }, []);

    useEffect(() => {
        fetchJobs();
    }, [statusFilter]);

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter !== 'ALL') {
                params.status = statusFilter;
            }
            const res = await axios.get(`${API_URL}/user/${userId}`, { params });
            setJobs(res.data);
        } catch {
            toast.error('Error fetching jobs');
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (job) => {
        setEditId(job.id);
        setFormData({
            title: job.title,
            description: job.description,
            company: job.company,
            location: job.location || '',
            type: job.type || '',
            no_of_openings: job.no_of_openings || 0,
            clientEmail: job.clientEmail || '',
            ctc: job.ctc || ''
        });
        setOriginalData({
            title: job.title,
            description: job.description,
            company: job.company,
            location: job.location || '',
            type: job.type || '',
            no_of_openings: job.no_of_openings || 0,
            clientEmail: job.clientEmail || '',
            ctc: job.ctc || ''
        });
        setShowEditModal(true);
    };

    const isDataChanged = () => {
        return Object.keys(formData).some(key => formData[key] !== originalData[key]);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!isDataChanged()) {
            toast.info('No changes found');
            return;
        }

        setActionLoading(true);
        try {
            await axios.put(`${API_URL}/${editId}`, formData);
            toast.success('Job updated successfully!');
            resetForm();
            fetchJobs();
        } catch {
            toast.error('Error updating job');
        } finally {
            setActionLoading(false);
        }
    };

    const confirmDelete = (id) => {
        setDeleteId(id);
        setShowDeleteConfirm(true);
    };

    const handleConfirmedDelete = async () => {
        setActionLoading(true);
        try {
            await axios.delete(`${API_URL}/${deleteId}`);
            toast.success('Job deleted successfully!');
            setShowDeleteConfirm(false);
            fetchJobs();
        } catch {
            toast.error('Error deleting job');
        } finally {
            setActionLoading(false);
        }
    };

    const resetForm = () => {
        setEditId(null);
        setShowEditModal(false);
        setOriginalData(null);
        setFormData({
            title: '',
            description: '',
            company: '',
            location: '',
            type: '',
            no_of_openings: 0,
            clientEmail: '',
            ctc: ''
        });
    };

    return (
        <div className='manage-jobs-container'>
            <h2 className='section-title'>Manage Job Postings</h2>

            <div className="filter-bar">
                <label>Status Filter:&nbsp;</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVE">Approved</option>
                    <option value="REJECT">Rejected</option>
                </select>
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                    <ClipLoader color="#36d7b7" loading={loading} size={40} />
                </div>
            ) : (
                <ul className='jobs-list'>
                    {jobs.map(job => (
                        <li className='job-item' key={job.id}>
                            <div className="job-header">
                                <strong>{job.title}</strong> at {job.company}
                            </div>
                            <p>{job.description}</p>
                            <div><strong>Client Email:</strong> {job.clientEmail}</div>
                            <div><strong>Annual CTC:</strong> {job.ctc}</div>
                            <div className="button-group">
                                <button className='btn edit' onClick={() => handleEditClick(job)}>Edit</button>
                                <button className='btn delete' onClick={() => confirmDelete(job.id)}>Delete</button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {showEditModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <form className='edit-form' onSubmit={handleUpdate}>
                            <h3>Update Job</h3>
                            <input type="text" name="title" placeholder="Title" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} required />
                            <textarea name="description" placeholder="Description" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} required />
                            <input type="text" name="company" placeholder="Company" value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} required />
                            <input type="text" name="location" placeholder="Location" value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} />
                            <input type="text" name="type" placeholder="Job Type" value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })} />
                            <input type="number" name="no_of_openings" placeholder="No. of Openings" value={formData.no_of_openings} onChange={e => setFormData({ ...formData, no_of_openings: parseInt(e.target.value) })} />
                            <input type="email" name="clientEmail" placeholder="Client Email" value={formData.clientEmail} onChange={e => setFormData({ ...formData, clientEmail: e.target.value })} required />
                            <input type="number" name="ctc" placeholder="CTC" value={formData.ctc} onChange={e => setFormData({ ...formData, ctc: parseFloat(e.target.value) })} required />

                            <div className="button-group">
                                <button type="submit" className='btn save' disabled={actionLoading}>
                                    {actionLoading ? 'Updating...' : 'Update'}
                                </button>
                                <button type="button" className='btn cancel' onClick={resetForm}>Cancel</button>
                            </div>

                            {actionLoading && (
                                <div style={{ marginTop: '10px', textAlign: 'center' }}>
                                    <ClipLoader color="#36d7b7" loading={actionLoading} size={30} />
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            )}

            {showDeleteConfirm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Are you sure you want to delete this job?</h3>
                        <div className="button-group">
                            <button className='btn delete' onClick={handleConfirmedDelete} disabled={actionLoading}>
                                {actionLoading ? 'Deleting...' : 'Yes'}
                            </button>
                            <button className='btn cancel' onClick={() => setShowDeleteConfirm(false)} disabled={actionLoading}>
                                No
                            </button>
                        </div>

                        {actionLoading && (
                            <div style={{ marginTop: '10px', textAlign: 'center' }}>
                                <ClipLoader color="#dc3545" loading={actionLoading} size={30} />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageJobs;
